import { createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TR, SUPPORTED_LANGS } from "./translations";

const LangCtx = createContext(null);

export const LANG_CODES = SUPPORTED_LANGS.map((l) => l.code);
export const DEFAULT_LANG = "en";

// ---- helpers ----
export function isLangCode(code) {
  return LANG_CODES.includes(code);
}

export function stripLangFromPath(pathname) {
  const parts = (pathname || "/").split("/");
  if (parts.length >= 2 && isLangCode(parts[1])) {
    parts.splice(1, 1);
    const p = parts.join("/") || "/";
    return p.startsWith("/") ? p : `/${p}`;
  }
  return pathname || "/";
}

export function getLangFromPath(pathname) {
  const parts = (pathname || "/").split("/");
  if (parts.length >= 2 && isLangCode(parts[1])) return parts[1];
  return null;
}

export function withLang(lang, to) {
  // Accept either a string path (may include ?query) or a {pathname, search, hash} object.
  if (!to) to = "/";
  const target = isLangCode(lang) ? lang : DEFAULT_LANG;
  if (typeof to === "string") {
    const [pathAndSearch, hash] = to.split("#");
    const [path, search] = pathAndSearch.split("?");
    const clean = stripLangFromPath(path.startsWith("/") ? path : `/${path}`);
    const base = clean === "/" ? `/${target}` : `/${target}${clean}`;
    return base + (search ? `?${search}` : "") + (hash ? `#${hash}` : "");
  }
  const path = stripLangFromPath(to.pathname || "/");
  return {
    ...to,
    pathname: path === "/" ? `/${target}` : `/${target}${path}`,
  };
}

function detectPreferredLang() {
  const saved = typeof window !== "undefined" && localStorage.getItem("kha_lang");
  if (saved && isLangCode(saved)) return saved;
  const nav = typeof navigator !== "undefined" ? (navigator.language || navigator.userLanguage || "") : "";
  const two = (nav.split("-")[0] || "").toLowerCase();
  if (isLangCode(two)) return two;
  return DEFAULT_LANG;
}

export function LangProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Language is DERIVED from the URL. If not present, redirect once to a language-prefixed URL.
  const urlLang = getLangFromPath(location.pathname);
  const lang = isLangCode(urlLang) ? urlLang : detectPreferredLang();

  // Update <html lang> / <html dir> + persist selection.
  useEffect(() => {
    const cfg = SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0];
    document.documentElement.lang = lang;
    document.documentElement.dir = cfg.dir;
    try { localStorage.setItem("kha_lang", lang); } catch (_e) { /* ignore */ }
  }, [lang]);

  // Redirect legacy (no-lang) URLs to their language-prefixed equivalent.
  useEffect(() => {
    if (urlLang) return;
    // Do NOT rewrite /admin paths — those are language-agnostic.
    if (location.pathname.startsWith("/admin")) return;
    const target = withLang(lang, { pathname: location.pathname, search: location.search, hash: location.hash });
    navigate(target, { replace: true });
  }, [urlLang, lang, location.pathname, location.search, location.hash, navigate]);

  const setLang = useCallback(
    (next) => {
      if (!isLangCode(next)) return;
      try { localStorage.setItem("kha_lang", next); } catch (_e) { /* ignore */ }
      if (location.pathname.startsWith("/admin")) return; // admin ignores lang prefix
      const target = withLang(next, { pathname: location.pathname, search: location.search, hash: location.hash });
      navigate(target);
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let cur = TR[lang] || TR.en;
      for (const p of parts) {
        if (cur == null) return path;
        cur = cur[p];
      }
      return cur ?? path;
    },
    [lang]
  );

  const dir = (SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0]).dir;

  const value = useMemo(
    () => ({ lang, setLang, t, dir, languages: SUPPORTED_LANGS }),
    [lang, setLang, t, dir]
  );

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
